import { apiGet, operationsClient } from '@/lib/api';
import { invoiceEndpoints } from '@/lib/api/endpoints';
import { useApiQuery } from '@/hooks/useApiQuery';
import type { EstimateDocLayout } from '@/types/estimateDocTemplate';

/**
 * The block layout an invoice's PDF renders through, resolved by the SERVER
 * (`GET /invoices/:id/document-layout`) — the same contract the estimate uses
 * (per-tenant gate → send-snapshot → chosen design → tenant default → classic).
 * The office preview/download read this instead of re-resolving client-side, so
 * they never diverge from the invoice the customer receives.
 *
 * Returns `{ layout: null }` when the invoice carries no design (classic PDF).
 *
 * `overrideDocTemplateId` previews an unsaved design pick from the invoice form:
 * `undefined` resolves the invoice as saved, `null` the tenant default, and a
 * design id resolves that specific design.
 */
export function useInvoiceResolvedDocLayout(
  invoiceId: string,
  enabled = true,
  overrideDocTemplateId?: string | null,
) {
  const previewDesign =
    overrideDocTemplateId === undefined ? undefined : (overrideDocTemplateId ?? 'default');
  return useApiQuery<{ layout: EstimateDocLayout | null }>({
    queryKey: ['invoices', invoiceId, 'document-layout', previewDesign ?? 'saved'],
    queryFn: () =>
      apiGet<{ layout: EstimateDocLayout | null }>(
        operationsClient,
        invoiceEndpoints.documentLayout(invoiceId, previewDesign),
      ),
    enabled: enabled && Boolean(invoiceId),
    staleTime: 60_000,
    retry: false,
    showErrorToast: false,
  });
}
